// --------------------- Import Globais ---------------------
import { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
// --------------------- Import Antd-Design ---------------------
import {
  Col,
  Flex,
  Row,
  Button,
  MenuProps,
  Skeleton,
  Table,
  Typography,
} from "antd";
import type { TableProps } from "antd/es/table";

// --------------------- Import Material Icon ---------------------
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

// --------------------- Import Styles ---------------------
import { Container } from "./styles";

// --------------------- Import Hooks ---------------------
import { useBuscarPostagens } from "./hooks/useBuscarPostagens";

// --------------------- Import Modais ---------------------
import ModalNotas from "./components/ModalNotas";
import ModalEditar from "./components/ModalEditar";
import ModalExcluir from "./components/ModalExcluir";
import ModalAdicionar from "./components/ModalAdicionar";

// --------------------- Import Components ---------------------
import { Columns } from "./components/Columns";

// --------------------- Import Uteis ---------------------
import { usePermissao } from "../../uteis/permissaoAdm/usePermissao";
import ModalCopiar from "./components/ModalCopiar";

// ---------------------  ---------------------
export default function ViewGerenciarPostagens() {
  // Traz o estilo Title da biblioteca
  const { Title } = Typography;
  const isMobile = useMediaQuery("(max-width:600px)");

  // Armazena o valor da origem do lead
  const [postagemId, setPostagemId] = useState<number | null>(null);
  const [observacao, setObservacao] = useState<string | null>(null);

  // Verificação de permissão do usuário, NDA = 0 - SEM PERMISSÃO, NDA = 1 - COM PERMISSÃO
  // Definação do Loading caso seja permitido
  const { isLoading: isTokenLoading, isValid } = usePermissao("OPE");

  const { postagens, isLoading, fetch } = useBuscarPostagens();

  // Controle de Paginação
  const [currentPage, setCurrentPage] = useState(1);

  // Inicializando o filtro
  const [filteredInfo, setFilteredInfo] = useState<{
    nome?: string[] | null;
    telefone?: string[] | null;
    tipo?: string[] | null;
    nomeEmpreend?: string[] | null;
    tipoInteresse?: string[] | null;
    troca?: string[] | null;
    aluguel?: string[] | null;
    compra?: string[] | null;
    venda?: string[] | null;
    dataIni?: string[] | null;
    dataFim?: string[] | null;
    fonte?: string[] | null;
    grupo?: string[] | null;
    dataDivulgacao?: string[] | null;
  }>({
    nome: null,
    telefone: null,
    tipo: null,
    nomeEmpreend: null,
    tipoInteresse: null,
    troca: null,
    aluguel: null,
    compra: null,
    venda: null,
    dataIni: null,
    dataFim: null,
    fonte: null,
    grupo: null,
    dataDivulgacao: null,
  });

  const [semFiltros, setSemFiltros] = useState(true);

  useEffect(() => {
    const filtros = localStorage.getItem("filtrosPropriedades");
    const pagina = localStorage.getItem("pageGerPropriedades");

    if (filtros) {
      const novoFiltro = JSON.parse(filtros);
      setFilteredInfo({
        nome: novoFiltro.nome ? [novoFiltro.nome] : null,
        telefone: novoFiltro.telefone ? [novoFiltro.telefone] : null,
        tipo: novoFiltro.tipo ? [novoFiltro.tipo] : null,
        nomeEmpreend: novoFiltro.nomeEmpreend
          ? [novoFiltro.nomeEmpreend]
          : null,
        tipoInteresse: novoFiltro.tipoInteresse
          ? [novoFiltro.tipoInteresse]
          : null,
        troca: novoFiltro.troca ? [novoFiltro.troca] : null,
        aluguel: novoFiltro.aluguel ? [novoFiltro.aluguel] : null,
        compra: novoFiltro.compra ? [novoFiltro.compra] : null,
        venda: novoFiltro.venda ? [novoFiltro.venda] : null,
        dataIni: novoFiltro.dataIni ? [novoFiltro.dataIni] : null,
        dataFim: novoFiltro.dataFim ? [novoFiltro.dataFim] : null,
        fonte: novoFiltro.fonte ? [novoFiltro.fonte] : null,
        grupo: novoFiltro.grupo ? [novoFiltro.grupo] : null,
        dataDivulgacao: novoFiltro.dataDivulgacao
          ? [novoFiltro.dataDivulgacao]
          : null,
      });
      setSemFiltros(false);
    }

    if (pagina) {
      setCurrentPage(Number(pagina));
    }
  }, []);

  const onChange: TableProps<any>["onChange"] = (pagination, filters) => {
    setFilteredInfo({
      nome: filters.nome as string[] | null,
      telefone: filters.telefone as string[] | null,
      tipo: filters.tipo as string[] | null,
      nomeEmpreend: filters.nomeEmpreend as string[] | null,
      tipoInteresse: filters.tipoInteresse as string[] | null,
      troca: filters.troca as string[] | null,
      aluguel: filters.aluguel as string[] | null,
      compra: filters.compra as string[] | null,
      venda: filters.venda as string[] | null,
      dataIni: filters.dataIni as string[] | null,
      dataFim: filters.dataFim as string[] | null,
      fonte: filters.fonte as string[] | null,
      grupo: filters.grupo as string[] | null,
      dataDivulgacao: filters.dataDivulgacao as string[] | null,
    });

    const hasFilters = Boolean(
      filters.nome ||
        filters.telefone ||
        filters.tipo ||
        filters.nomeEmpreend ||
        filters.tipoInteresse ||
        filters.troca ||
        filters.aluguel ||
        filters.compra ||
        filters.venda ||
        filters.dataIni ||
        filters.dataFim ||
        filters.fonte ||
        filters.grupo ||
        filters.dataDivulgacao
    );
    setSemFiltros(!hasFilters);

    if (pagination.current) {
      localStorage.setItem(
        "pageGerPropriedades",
        pagination.current.toString()
      );
    }

    localStorage.setItem("filtrosPropriedades", JSON.stringify(filters));
  };

  const removerFiltros = () => {
    localStorage.removeItem("filtrosPropriedades");
    localStorage.removeItem("pageGerPropriedades");

    setFilteredInfo({
      nome: null,
      telefone: null,
      tipo: null,
      nomeEmpreend: null,
      tipoInteresse: null,
      troca: null,
      aluguel: null,
      compra: null,
      venda: null,
      dataIni: null,
      dataFim: null,
      fonte: null,
      grupo: null,
      dataDivulgacao: null,
    });
    setSemFiltros(true);
    setCurrentPage(1);
  };

  const handleFilter = () => {
    removerFiltros();
  };

  // OnClick das opções e funções que serão realizadas do item Ações
  const onClick: MenuProps["onClick"] = async ({ key }) => {
    switch (key) {
      // Editar
      case "1":
        editarModal();
        break;

      case "2":
        // Excluir
        excluirModal();
        break;

      case "3":
        // Notas
        notasModal();
        break;

      case "4":
        // Copiar
        copiarModal();
        break;
    }
  };

  // Traz as informações  OnClick, origem , nome e id do apartamento além do nome do empreendimento, ao clickar na linha especifica
  const columns = Columns(onClick, setPostagemId, setObservacao, {
    nome: filteredInfo.nome,
    telefone: filteredInfo.telefone,
    tipo: filteredInfo.tipo,
    nomeEmpreend: filteredInfo.nomeEmpreend,
    tipoInteresse: filteredInfo.tipoInteresse,
    troca: filteredInfo.troca,
    aluguel: filteredInfo.aluguel,
    compra: filteredInfo.compra,
    venda: filteredInfo.venda,
    dataIni: filteredInfo.dataIni,
    dataFim: filteredInfo.dataFim,
    fonte: filteredInfo.fonte,
    grupo: filteredInfo.grupo,
    dataDivulgacao: filteredInfo.dataDivulgacao,
  });

  // State de abertura e fechamento do modal

  const [isModalAdicionarOpen, setIsModalAdicionarOpen] = useState(false);

  const [isModalNotasOpen, setIsModalNotasOpen] = useState(false);

  const [isModalEditarOpen, setIsModalEditarOpen] = useState(false);

  const [isModalExcluirOpen, setIsModalExcluirOpen] = useState(false);

  const [isModalCopiarOpen, setIsModalCopiarOpen] = useState(false);

  // Funçao para abrir o modal (Notas)
  const notasModal = () => {
    setIsModalNotasOpen(true);
  };

  // Funçao para fechar o modal (Notas)
  const handleNotasFechar = () => {
    setIsModalNotasOpen(false);
  };

  // Funçao para abrir o modal (Editar)
  const editarModal = () => {
    setIsModalEditarOpen(true);
  };
  const handleEditarFechar = () => {
    setIsModalEditarOpen(false);
  };

  const excluirModal = () => {
    setIsModalExcluirOpen(true);
  };
  const handleExcluirFechar = () => {
    setIsModalExcluirOpen(false);
  };

  const adicionarModal = () => {
    setIsModalAdicionarOpen(true);
  };
  const handleAdicionarFechar = () => {
    setIsModalAdicionarOpen(false);
  };

  const copiarModal = () => {
    setIsModalCopiarOpen(true);
  };
  const handleCopiarFechar = () => {
    setIsModalCopiarOpen(false);
  };

  return (
    <>
      {(isLoading && isTokenLoading) || !isValid ? (
        <Skeleton active />
      ) : (
        <Container>
          <Row>
            <Col span={5}>
              <Flex align="center" justify="center" style={{ marginTop: 15 }}>
                <a href={`/inicio`}>
                 
                </a>
              </Flex>
            </Col>
            <Col span={12}>
              <Flex align="center" justify="center">
                <HomeIcon color="primary" fontSize="large" />
                <Title
                  style={{
                    marginLeft: 10,
                    marginTop: 10,
                    fontSize: isMobile ? "1rem" : "2rem",
                    color: "rgb(0, 54, 68)",
                  }}
                  level={3}
                >
                  Gerenciar Postagens
                </Title>
              </Flex>
            </Col>
          </Row>
          <Row>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end", // Alinha o conteúdo à direita
                alignItems: "center",
                height: "auto",
                width: "90%", // Garante que ocupe toda a largura disponível
                gap: "20px", // Espaço entre os botões
              }}
            >
              <Button
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "auto",
                }}
                onClick={() => {
                  adicionarModal();
                }}
                type="primary"
              >
                + Postagem
              </Button>

              {semFiltros ? (
                <Button onClick={handleFilter} type="primary" disabled={true}>
                  <FilterAltIcon />
                </Button>
              ) : (
                <Button onClick={handleFilter} type="primary">
                  <FilterAltOffIcon />
                </Button>
              )}
            </div>
            <Col span={isMobile ? 5 : 1}></Col>
            <Col
              span={24}
              style={{
                marginTop: isMobile ? 15 : 15,
                marginBottom: isMobile ? 30 : 50,
              }}
            >
              <Flex
                style={{
                  width: isMobile ? "auto" : "100%",
                  overflow: "auto",
                }}
              >
                <Table
                  columns={columns}
                  dataSource={postagens}
                  onChange={onChange}
                  bordered
                  style={{
                    alignItems: "center",
                    justifyContent: "center",
                    width: isMobile ? "auto" : "100%",
                    maxWidth: isMobile ? "none" : "100%",
                  }}
                  pagination={{ defaultCurrent: currentPage }}
                />
              </Flex>
            </Col>
            <Col span={1}></Col>
          </Row>
          <ModalAdicionar
            isModalOpen={isModalAdicionarOpen}
            handleFechar={handleAdicionarFechar}
            fetch={fetch}
          />
          <ModalNotas
            isModalOpen={isModalNotasOpen}
            handleFechar={handleNotasFechar}
            postagemId={postagemId}
            observacao={observacao}
          />
          <ModalEditar
            isModalOpen={isModalEditarOpen}
            handleFechar={handleEditarFechar}
            postagemId={postagemId}
          />
          <ModalExcluir
            isModalOpen={isModalExcluirOpen}
            handleFechar={handleExcluirFechar}
            postagemId={postagemId}
          />
          <ModalCopiar
            isModalOpen={isModalCopiarOpen}
            handleFechar={handleCopiarFechar}
            postagemId={postagemId}
            fetch={fetch}
          />
        </Container>
      )}
      ;
    </>
  );
}
